import { useEffect, useState } from "react";
import { Grid, Typography } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {  ImportStatus, Scenario } from "src/types";
import { TrafficEmissionImportForm, TrafficEmissionImportFormData } from "src/forms/TrafficEmissionImportForm";
import {
  TrafficEmissionImportMutationData,
  useTrafficEmissionImportMutation,
} from "src/hooks/useTrafficEmissionImport";
import { useScenario } from "src/hooks/useScenario";
import { ImportFailed } from "src/components/ImportFailed";
import { ImportFinished } from "src/components/ImportFinished";
import { Pane } from "src/components/Pane";
import { usePrevious } from "src/hooks";

interface Props {}

export function TrafficEmissionImportPage(props: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { scenarioId = "", caseId = "", emissionId = ""} = useParams<{
    caseId: string;
    scenarioId: string;
    emissionId: string;
  }>();

  const [refetchInterval, setRefetchInterval] = useState<false | number>(false);
  const { data: scenario } = useScenario(scenarioId, {
    refetchInterval,
  });
  const previousImportStatus = usePrevious(scenario?.networkImportStatus);
  const importMutation = useTrafficEmissionImportMutation(scenarioId);

  /**
   * Effects
   */
  useEffect(updateRefreshInterval, [scenario]);
  useEffect(actUponFinishedImportStatus, [navigate, location, previousImportStatus, scenario]);

  /**
   * Functions
   */
  function updateRefreshInterval() {
    if (scenario?.networkImportStatus === ImportStatus.IMPORTING) {
      setRefetchInterval(1000);
    } else setRefetchInterval(false);
  }

  function actUponFinishedImportStatus() {
    // first check the previous import status
    if (previousImportStatus === undefined) return;
    if (previousImportStatus === ImportStatus.FINISHED) return;

    // if the new status is finished, act upon it
    if (scenario?.networkImportStatus === ImportStatus.FINISHED) {
      navigate({...location}, { state: { action: "rerenderWMS" } });
    }
  }

  async function handleSave(data: TrafficEmissionImportFormData) {
    if (!data.segmentFile) {
      console.warn("No 'pointSourceFile' field submitted, this should be fixed.");
      return;
    }
    await importMutation.mutateAsync(data as TrafficEmissionImportMutationData);
  }

  function handleClose() {
    navigate(`/cases/${caseId}/scenarios/${scenarioId}/emissions/traffic/${emissionId}`);
  }

  // render the component
  return render();

  function render() {
    if (!scenario) return null;

    return (
      <Pane width={400}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {/* TODO translate */}
            <Typography variant="h2">{t("label.bulkUploadTrafficEmissionData")}</Typography>
          </Grid>

          {scenario.networkImportStatus !== ImportStatus.IMPORTING && renderForm(scenario)}

          {scenario.networkImportStatus === ImportStatus.IMPORTING && (
            <Grid item xs={12}>
              <Typography variant="h2">{t(`label.importStatus.${ImportStatus.IMPORTING}`)}</Typography>
            </Grid>
          )}
        </Grid>
      </Pane>
    );
  }

  function renderForm(scenario: Scenario) {
    return (
      <Grid item xs={12}>
        <TrafficEmissionImportForm
          key={scenario.networkImportStatus}
          data={{ segmentFile: null }}
          onClose={handleClose}
          onSubmit={handleSave}
          isLoading={importMutation.isLoading}
          errors={importMutation.error?.response?.data}
          statusBody={
            <Grid item xs={12}>
              {renderStatusBody(scenario)}
            </Grid>
          }
        />
      </Grid>
    );
  }

  function renderStatusBody(scenario: Scenario) {
    if (scenario.networkImportStatus === ImportStatus.FINISHED)
      return <ImportFinished importWarnings={scenario.networkImportWarnings} />;
    if (scenario.networkImportStatus === ImportStatus.FAILED)
      return <ImportFailed importErrors={scenario.networkImportErrors} />;
    else return null;
  }
}
